import type {
  Agent,
  CreateAgent,
  CreateHumanAgent,
  CreateMetabaseDashboardUrlRequest,
  CreateWhatsAppPhoneNumber,
  HumanAgent,
  UpdateWhatsAppPhoneNumber,
  WhatsAppAgent,
} from "@/types/agents";
import { agentHttpClient, handleAxiosError } from "@/lib/http";

export const getAgents = async (
  projectId: string | null | undefined,
  accessToken: string,
  login: () => void
) => {
  try {
    const params: { [id: string]: string } = {};

    if (projectId) {
      params["medplum_project_id"] = projectId;
    }

    const response = await agentHttpClient.get<{ agents: Agent[] }>(
      `/v1/agents`,
      {
        params: params,
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    return response.data.agents;
  } catch (error) {
    handleAxiosError(error, login);
  }
};

export const createAgent = async (
  data: CreateAgent,
  accessToken: string,
  login: () => void
) => {
  try {
    const response = await agentHttpClient.post<{ agent: Agent }>(
      `/v1/agents`,
      data,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
    return response.data.agent;
  } catch (error) {
    handleAxiosError(error, login);
  }
};

export const getHumanAgents = async (
  agentId: string,
  accessToken: string,
  login: () => void
) => {
  try {
    const response = await agentHttpClient.get<{ human_agents: HumanAgent[] }>(
      `/v1/agents/${agentId}/channels/human_agent/agents`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    return response.data.human_agents;
  } catch (error) {
    handleAxiosError(error, login);
  }
};

export const createHumanAgent = async (
  agentId: string,
  humanAgent: CreateHumanAgent,
  accessToken: string,
  login: () => void
) => {
  try {
    return await agentHttpClient.post<{ human_agent: HumanAgent }>(
      `/v1/agents/${agentId}/channels/human_agent/agents`,
      humanAgent,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
  } catch (error) {
    handleAxiosError(error, login);
  }
};

export const getWAPhoneNumbers = async (
  agentId: string,
  accessToken: string,
  login: () => void
) => {
  try {
    const response = await agentHttpClient.get<{
      whatsapp_phone_numbers: WhatsAppAgent[];
    }>(`/v1/agents/${agentId}/channels/whatsapp/phone_numbers`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    return response.data.whatsapp_phone_numbers;
  } catch (error) {
    handleAxiosError(error, login);
  }
};

export const createWAPhoneNumber = async (
  agentId: string,
  data: CreateWhatsAppPhoneNumber,
  accessToken: string,
  login: () => void
) => {
  try {
    return await agentHttpClient.post<{ agent: WhatsAppAgent }>(
      `/v1/agents/${agentId}/channels/whatsapp/phone_numbers`,
      data,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
  } catch (error) {
    handleAxiosError(error, login);
  }
};

export const updateWAPhoneNumber = async (
  agentId: string,
  phoneNumberId: string,
  data: UpdateWhatsAppPhoneNumber,
  accessToken: string,
  login: () => void
) => {
  try {
    return await agentHttpClient.patch<{ agent: WhatsAppAgent }>(
      `/v1/agents/${agentId}/channels/whatsapp/phone_numbers/${phoneNumberId}`,
      data,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
  } catch (error) {
    handleAxiosError(error, login);
  }
};

export const getChatwootSsoLink = async (
  agentId: string,
  accessToken: string,
  login: () => void
) => {
  try {
    const response = await agentHttpClient.post<{ url: string }>(
      `/v1/agents/${agentId}/chatwoot/sso_link`,
      {},
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    return response.data.url;
  } catch (error) {
    handleAxiosError(error, login);
  }
};

export const createMetabaseDashboardUrl = async (
  agentId: string,
  request: CreateMetabaseDashboardUrlRequest,
  accessToken: string,
  login: () => void
) => {
  try {
    const response = await agentHttpClient.post<{
      url?: string;
    }>(`/v1/agents/${agentId}/metabase/dashboard_url`, request, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    return response.data.url;
  } catch (error) {
    handleAxiosError(error, login);
  }
};
